import clsx from 'clsx';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, IconButton } from '../../../ui/v2';
import { Callout, EmptyState, Reason } from '../../../ui/v2';
import { Checkbox, Field, Input, Radio, Textarea } from '../../../ui/v2';
import { ConfirmDialog, Dialog, RowMenu } from '../../../ui/v2';
import { Tag } from '../../../ui/v2';
import {
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeletonRows,
} from '../../../ui/v2';
import { Segmented } from '../../../ui/v2';
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
} from '../../../ui/v2';
import {
  combineQueries,
  useBlackoutActions,
  useOpenTableQuery,
  useStoreInfoQuery,
} from './data';
import {
  type AreaBlackout,
  type BlackoutDraft,
  type DiningArea,
  addDays,
  allDayEndDateExclusive,
  areaDisplayName,
  blackoutPreview,
  combineLocalDateTime,
  emptyBlackoutDraft,
  formatBlackoutWindow,
  formatDate,
  isAllDayBlackout,
  localDate,
  localTime,
} from './model';
import {
  QueryGate,
  SectionBody,
  SectionHead,
  SectionTitleRow,
  TwoLine,
  useSavedAt,
  SavedNote,
} from './shared';

/**
 * Reservations — OpenTable area blackouts.
 *
 * The section is gated three ways, like production: no OpenTable
 * connection, connected but no dining areas yet, or a load error. With
 * areas, the body is a month calendar (or a list) beside the upcoming
 * blackouts. Both open the same editor: click a day to add, click a
 * blackout to edit. Deleting asks for confirmation.
 *
 * Proposals vs production (labelled in the spec): Month + List instead of
 * Month + Week; no per-area colour coding — the chip names the area.
 */
const ReservationsSection: React.FC = () => {
  const openTable = useOpenTableQuery();
  const info = useStoreInfoQuery();
  const blackoutActions = useBlackoutActions();
  const q = combineQueries(
    [openTable, info],
    () => ({ ...openTable.data!, today: info.data!.today_local_date }),
    'Could not load OpenTable blackout settings.',
  );
  const [editing, setEditing] = useState<BlackoutDraft | null>(null);
  const [deleting, setDeleting] = useState<AreaBlackout | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, markSaved] = useSavedAt();
  const [savedText, setSavedText] = useState('Saved.');

  const areas = q.data?.areas ?? [];
  const today = q.data?.today ?? '';
  const ready =
    q.status === 'success' && q.data!.hasActiveConnection && areas.length > 0;
  const addBlocked =
    q.status === 'success' && !q.data!.hasActiveConnection
      ? 'Connect OpenTable first.'
      : q.status === 'success' && areas.length === 0
        ? 'Waiting for dining areas.'
        : undefined;

  const openAdd = (overrides: Partial<BlackoutDraft> = {}) => {
    setEditing(emptyBlackoutDraft(today, overrides));
    setError(null);
  };
  const openEdit = (b: AreaBlackout) => {
    const allDay = isAllDayBlackout(b);
    setEditing({
      id: b.id,
      start_date: localDate(b.starts_at_local),
      end_date: allDay ? allDayEndDateExclusive(b) : localDate(b.ends_at_local),
      start_time: allDay ? '00:00' : localTime(b.starts_at_local),
      end_time: allDay ? '00:00' : localTime(b.ends_at_local),
      all_day: allDay,
      dining_area_ids: [b.dining_area_id],
      reason: b.reason,
    });
    setError(null);
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.start_date || !editing.end_date)
      return setError('Select start and end dates.');
    if (editing.dining_area_ids.length === 0)
      return setError('Select at least one dining area.');
    if (!editing.reason.trim())
      return setError('Add a reason Arthur can explain to callers.');
    if (!editing.all_day && (!editing.start_time || !editing.end_time))
      return setError('Add a start and end time.');
    const starts = combineLocalDateTime(
      editing.start_date,
      editing.all_day ? '00:00' : editing.start_time,
    );
    const ends = combineLocalDateTime(
      editing.end_date,
      editing.all_day ? '00:00' : editing.end_time,
    );
    if (ends <= starts)
      return setError(
        editing.all_day
          ? 'End date must be after start date.'
          : 'End date and time must be after start date and time.',
      );
    setBusy(true);
    setError(null);
    const body = {
      starts_at_local: starts,
      ends_at_local: ends,
      all_day: editing.all_day,
      reason: editing.reason.trim(),
      dining_area_ids: editing.dining_area_ids,
    };
    try {
      if (editing.id) await blackoutActions.update(editing.id, body);
      else await blackoutActions.create(body);
      setSavedText(
        editing.id
          ? 'Saved.'
          : editing.dining_area_ids.length > 1
            ? `Added ${editing.dining_area_ids.length} blackouts.`
            : 'Blackout added.',
      );
      setEditing(null);
      markSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save blackout.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    setError(null);
    try {
      await blackoutActions.delete(deleting.id);
      setDeleting(null);
      setEditing(null);
      setSavedText('Blackout deleted.');
      markSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete blackout.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionBody measure="reservations">
      <SectionHead
        title="Reservations"
        description="Block OpenTable dining areas for private events or maintenance. Arthur stops offering those areas for the window and can explain why."
        action={
          <>
            <SavedNote when={savedAt}>{savedText}</SavedNote>
            <div className="flex flex-col items-end gap-1">
              <Button
                size="sm"
                variant="primary"
                onClick={() => openAdd()}
                disabledReason={
                  ready
                    ? undefined
                    : (addBlocked ??
                      'Available once OpenTable dining areas are loaded.')
                }
              >
                Add blackout
              </Button>
              {addBlocked && <Reason>{addBlocked}</Reason>}
            </div>
          </>
        }
      />
      <div className="mt-6">
        <QueryGate query={q} skeleton={<CalendarSkeleton />}>
          {(data) =>
            !data.hasActiveConnection ? (
              <EmptyState
                compact
                icon={<IconCalendar />}
                title="Connect OpenTable to manage blackouts"
                description="Area blackouts come from your OpenTable connection. Once it's connected, dining areas appear here."
                action={
                  <Link
                    to="/integrations"
                    className="inline-flex h-7 items-center rounded-v2 border border-v2-line-strong/80 bg-v2-surface px-2.5 text-v2-12 font-medium text-v2-fg hover:bg-v2-sunken"
                  >
                    Open integrations
                  </Link>
                }
              />
            ) : data.areas.length === 0 ? (
              <EmptyState
                compact
                icon={<IconCalendar />}
                title="No dining areas yet"
                description="OpenTable is connected. Areas appear here after availability has been searched for the first time — usually after Arthur's first reservation call."
              />
            ) : (
              <div className="flex flex-col gap-8 2xl:flex-row 2xl:items-start 2xl:gap-10">
                <BlackoutCalendar
                  className="min-w-0 flex-1"
                  today={data.today}
                  blackouts={data.blackouts}
                  areas={data.areas}
                  onPickDay={(start, endExclusive) =>
                    openAdd({
                      start_date: start,
                      end_date: endExclusive,
                      all_day: true,
                      start_time: '00:00',
                      end_time: '00:00',
                    })
                  }
                  onOpen={openEdit}
                />
                <UpcomingList
                  className="2xl:w-[336px] 2xl:shrink-0"
                  blackouts={data.blackouts}
                  areas={data.areas}
                  onOpen={openEdit}
                  onDelete={setDeleting}
                  onAdd={() => openAdd()}
                />
              </div>
            )
          }
        </QueryGate>
      </div>

      <BlackoutDialog
        draft={editing}
        areas={areas}
        today={today}
        busy={busy}
        error={error}
        onChange={(d) => {
          setEditing(d);
          setError(null);
        }}
        onClose={() => !busy && setEditing(null)}
        onSave={save}
        onDelete={() => {
          const b = q.data?.blackouts.find((x) => x.id === editing?.id);
          if (b) setDeleting(b);
        }}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        title="Delete this blackout?"
        confirmLabel="Delete blackout"
        busy={busy}
        consequence={
          deleting
            ? `${areaDisplayName(deleting.dining_area_id, areas, deleting.dining_area_name)} becomes available again for ${formatBlackoutWindow(deleting)}. This can't be undone.`
            : ''
        }
      >
        {error && <Callout tone="danger">{error}</Callout>}
      </ConfirmDialog>
    </SectionBody>
  );
};

// ---------------------------------------------------------------------------
// Calendar (Store Info-specific)
// ---------------------------------------------------------------------------

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Every local date (inclusive) a blackout touches, for the month grid. */
function blackoutDays(b: AreaBlackout): string[] {
  const start = localDate(b.starts_at_local);
  const last = isAllDayBlackout(b)
    ? addDays(allDayEndDateExclusive(b), -1)
    : localDate(b.ends_at_local);
  const out: string[] = [];
  for (let d = start; d <= last && out.length < 62; d = addDays(d, 1))
    out.push(d);
  return out;
}

/**
 * BlackoutCalendar: a hand-rolled month grid in V2 tokens (no FullCalendar).
 * - Month view: 7 columns, Monday first, today marked, blackouts as quiet
 *   chips ("Patio · Private dinner"). Click a day to add, drag across days
 *   to add a multi-day all-day blackout, click a chip to edit.
 * - List view: the same blackouts as a table, for screens too narrow for a
 *   grid and for scanning far ahead.
 */
export const BlackoutCalendar: React.FC<{
  today: string;
  blackouts: AreaBlackout[];
  areas: DiningArea[];
  onPickDay: (start: string, endExclusive: string) => void;
  onOpen: (b: AreaBlackout) => void;
  className?: string;
}> = ({ today, blackouts, areas, onPickDay, onOpen, className }) => {
  const [view, setView] = useState<'month' | 'list'>('month');
  const [cursor, setCursor] = useState(() => today.slice(0, 7)); // YYYY-MM
  const [drag, setDrag] = useState<{ from: string; to: string } | null>(null);

  const [y, m] = cursor.split('-').map(Number);
  const first = `${cursor}-01`;
  const firstDow = (new Date(`${first}T00:00:00`).getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(y, m, 0).getDate();
  const gridStart = addDays(first, -firstDow);
  const cells = Array.from(
    { length: Math.ceil((firstDow + daysInMonth) / 7) * 7 },
    (_, i) => addDays(gridStart, i),
  );

  const byDay = useMemo(() => {
    const map = new Map<string, AreaBlackout[]>();
    blackouts.forEach((b) =>
      blackoutDays(b).forEach((d) => map.set(d, [...(map.get(d) ?? []), b])),
    );
    map.forEach((list) =>
      list.sort((a, b) => a.starts_at_local.localeCompare(b.starts_at_local)),
    );
    return map;
  }, [blackouts]);

  const inRange = (d: string) =>
    drag &&
    d >= (drag.from < drag.to ? drag.from : drag.to) &&
    d <= (drag.from < drag.to ? drag.to : drag.from);
  const finishDrag = () => {
    if (!drag) return;
    const a = drag.from < drag.to ? drag.from : drag.to;
    const b = drag.from < drag.to ? drag.to : drag.from;
    setDrag(null);
    onPickDay(a, addDays(b, 1));
  };
  const shift = (n: number) => {
    const d = new Date(y, m - 1 + n, 1);
    setCursor(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    );
  };

  const sorted = useMemo(
    () =>
      [...blackouts].sort((a, b) =>
        a.starts_at_local.localeCompare(b.starts_at_local),
      ),
    [blackouts],
  );

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          aria-label="Calendar view"
          items={[
            { key: 'month', label: 'Month' },
            { key: 'list', label: 'List' },
          ]}
          value={view}
          onChange={setView}
        />
        {view === 'month' && (
          <div className="ml-auto flex items-center gap-1">
            <IconButton
              size="sm"
              label="Previous month"
              onClick={() => shift(-1)}
            >
              <IconChevronLeft />
            </IconButton>
            <span className="v2-num min-w-[132px] text-center text-v2-13 font-medium">
              {MONTHS[m - 1]} {y}
            </span>
            <IconButton size="sm" label="Next month" onClick={() => shift(1)}>
              <IconChevronRight />
            </IconButton>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCursor(today.slice(0, 7))}
              disabled={cursor === today.slice(0, 7)}
            >
              Today
            </Button>
          </div>
        )}
      </div>

      {view === 'month' ? (
        <>
          <div
            role="grid"
            aria-label={`${MONTHS[m - 1]} ${y}`}
            className="mt-3 select-none border-t border-l border-v2-line"
            onMouseLeave={() => drag && finishDrag()}
          >
            <div role="row" className="grid grid-cols-7">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  role="columnheader"
                  className="h-7 border-b border-r border-v2-line px-1.5 text-v2-11 font-medium leading-7 text-v2-fg-3"
                >
                  {w}
                </div>
              ))}
            </div>
            <div role="row" className="grid grid-cols-7">
              {cells.map((d) => {
                const inMonth = d.slice(0, 7) === cursor;
                const isToday = d === today;
                const past = d < today;
                const list = byDay.get(d) ?? [];
                const shown = list.slice(0, 3);
                return (
                  <div
                    key={d}
                    role="gridcell"
                    aria-label={`${formatDate(d, { weekday: true })}${list.length ? `, ${list.length} blackout${list.length === 1 ? '' : 's'}` : ''}`}
                    className={clsx(
                      'group relative flex min-h-[88px] flex-col border-b border-r border-v2-line p-1 transition-colors',
                      !inMonth && 'bg-v2-sunken/40',
                      past
                        ? 'cursor-default'
                        : 'cursor-pointer hover:bg-v2-accent/[0.04]',
                      inRange(d) && 'bg-v2-accent/[0.08]',
                    )}
                    onMouseDown={(e) => {
                      if (past || e.button !== 0) return;
                      if ((e.target as HTMLElement).closest('button')) return;
                      setDrag({ from: d, to: d });
                    }}
                    onMouseEnter={() => drag && setDrag({ ...drag, to: d })}
                    onMouseUp={() => drag && finishDrag()}
                    onKeyDown={(e) => {
                      if (past) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPickDay(d, addDays(d, 1));
                      }
                    }}
                    tabIndex={past ? -1 : 0}
                  >
                    <span
                      className={clsx(
                        'v2-num inline-flex h-5 w-5 items-center justify-center rounded-full text-v2-11',
                        isToday
                          ? 'bg-v2-accent font-semibold text-v2-on-accent'
                          : inMonth
                            ? past
                              ? 'text-v2-fg-4'
                              : 'text-v2-fg-2'
                            : 'text-v2-fg-4',
                      )}
                    >
                      {Number(d.slice(8, 10))}
                    </span>
                    <div className="mt-0.5 flex flex-col gap-0.5">
                      {shown.map((b) => (
                        <button
                          key={`${b.id}-${d}`}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen(b);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          title={`${areaDisplayName(b.dining_area_id, areas, b.dining_area_name)} · ${formatBlackoutWindow(b)} · ${b.reason}`}
                          className={clsx(
                            'block w-full truncate rounded-v2-sm border-l-2 px-1 text-left text-v2-11 leading-[18px] transition-colors',
                            past
                              ? 'border-v2-fg-4/50 bg-v2-sunken text-v2-fg-3'
                              : 'border-v2-fg-2 bg-v2-sunken text-v2-fg hover:bg-v2-line/70',
                          )}
                        >
                          <span className="font-medium">
                            {areaDisplayName(
                              b.dining_area_id,
                              areas,
                              b.dining_area_name,
                            )}
                          </span>
                          {!isAllDayBlackout(b) &&
                            localDate(b.starts_at_local) === d && (
                              <span className="v2-num text-v2-fg-3">
                                {' '}
                                {localTime(b.starts_at_local)}
                              </span>
                            )}
                          <span className="text-v2-fg-3"> · {b.reason}</span>
                        </button>
                      ))}
                      {list.length > shown.length && (
                        <span className="px-1 text-v2-11 text-v2-fg-3">
                          +{list.length - shown.length} more
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : sorted.length === 0 ? (
        <EmptyState
          compact
          title="No blackouts"
          description="Add one to block a dining area for a window of time."
        />
      ) : (
        <Table
          fixed
          className="mt-3 [&_tr>*:first-child]:pl-3 lg:[&_tr>*:first-child]:pl-3 [&_tr>*:last-child]:pr-3 lg:[&_tr>*:last-child]:pr-3"
        >
          <THead>
            <tr>
              <TH width={160}>Area</TH>
              <TH width={260}>When</TH>
              <TH>Reason</TH>
            </tr>
          </THead>
          <TBody>
            {sorted.map((b) => {
              const past = b.ends_at_local < `${today}T00:00:00`;
              return (
                <TR
                  key={b.id}
                  onActivate={() => onOpen(b)}
                  className={clsx(past && 'text-v2-fg-3')}
                >
                  <TD truncate className="font-medium">
                    {areaDisplayName(
                      b.dining_area_id,
                      areas,
                      b.dining_area_name,
                    )}
                    {past && <Tag className="ml-2">Past</Tag>}
                  </TD>
                  <TD num truncate>
                    {formatBlackoutWindow(b)}
                  </TD>
                  <TD truncate>{b.reason}</TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      )}
    </div>
  );
};

const UpcomingList: React.FC<{
  blackouts: AreaBlackout[];
  areas: DiningArea[];
  onOpen: (b: AreaBlackout) => void;
  onDelete: (b: AreaBlackout) => void;
  onAdd: () => void;
  className?: string;
}> = ({ blackouts, areas, onOpen, onDelete, onAdd, className }) => {
  const now = new Date();
  const upcoming = useMemo(
    () =>
      blackouts
        .filter((b) => new Date(b.ends_at_local).getTime() >= now.getTime())
        .sort((a, b) => a.starts_at_local.localeCompare(b.starts_at_local)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blackouts],
  );
  return (
    <div className={className}>
      <SectionTitleRow title="Upcoming" count={upcoming.length} />
      {upcoming.length === 0 ? (
        <EmptyState
          compact
          title="Nothing scheduled"
          description="Arthur offers every dining area as OpenTable allows."
          action={
            <Button size="sm" variant="secondary" onClick={onAdd}>
              Add blackout
            </Button>
          }
        />
      ) : (
        <ul className="mt-1 border-t border-v2-line">
          {upcoming.map((b) => (
            <li
              key={b.id}
              className="flex items-center gap-2 border-b border-v2-line/80 py-1.5"
            >
              <button
                type="button"
                onClick={() => onOpen(b)}
                className="flex min-w-0 flex-1 items-center rounded-v2 px-1 py-1 text-left hover:bg-v2-sunken/70"
              >
                <TwoLine
                  primary={
                    <>
                      {areaDisplayName(
                        b.dining_area_id,
                        areas,
                        b.dining_area_name,
                      )}
                      <span className="font-normal text-v2-fg-3">
                        {' '}
                        · {b.reason}
                      </span>
                    </>
                  }
                  secondary={
                    <span className="v2-num">{formatBlackoutWindow(b)}</span>
                  }
                />
              </button>
              <RowMenu
                label={`Actions for ${areaDisplayName(b.dining_area_id, areas, b.dining_area_name)} blackout`}
                onEdit={() => onOpen(b)}
                onDelete={() => onDelete(b)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CalendarSkeleton: React.FC = () => (
  <div className="flex flex-col gap-8 2xl:flex-row 2xl:gap-10" aria-busy>
    <div className="flex-1">
      <div className="v2-skeleton h-8 w-40" />
      <div className="mt-3 grid grid-cols-7 gap-px border border-v2-line bg-v2-line">
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={i} className="min-h-[88px] bg-v2-surface p-1">
            <span className="v2-skeleton inline-block h-3 w-4" />
          </div>
        ))}
      </div>
    </div>
    <div className="2xl:w-[336px]">
      <Table
        fixed
        className="[&_tr>*:first-child]:pl-0 lg:[&_tr>*:first-child]:pl-0"
      >
        <tbody>
          <TableSkeletonRows rows={4} cols={1} />
        </tbody>
      </Table>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

const BlackoutDialog: React.FC<{
  draft: BlackoutDraft | null;
  areas: DiningArea[];
  today: string;
  busy: boolean;
  error: string | null;
  onChange: (d: BlackoutDraft) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
}> = ({
  draft,
  areas,
  today,
  busy,
  error,
  onChange,
  onClose,
  onSave,
  onDelete,
}) => {
  const isEdit = !!draft?.id;
  const set = (patch: Partial<BlackoutDraft>) =>
    draft && onChange({ ...draft, ...patch });
  const toggleArea = (areaId: number) => {
    if (!draft) return;
    if (isEdit) return set({ dining_area_ids: [areaId] });
    const s = new Set(draft.dining_area_ids);
    if (s.has(areaId)) s.delete(areaId);
    else s.add(areaId);
    set({ dining_area_ids: Array.from(s).sort((a, b) => a - b) });
  };
  return (
    <Dialog
      open={draft !== null}
      onClose={onClose}
      title={isEdit ? 'Edit blackout' : 'Add blackout'}
      description={
        isEdit
          ? 'A blackout covers one dining area. Change the window or the reason.'
          : 'Pick the areas and the window. One blackout is created per area.'
      }
      size="md"
      footer={
        <>
          {isEdit && (
            <Button
              variant="danger"
              className="mr-auto"
              onClick={onDelete}
              disabled={busy}
            >
              Delete…
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSave} loading={busy}>
            {isEdit
              ? 'Save'
              : draft && draft.dining_area_ids.length > 1
                ? `Add ${draft.dining_area_ids.length} blackouts`
                : 'Add blackout'}
          </Button>
        </>
      }
    >
      {draft && (
        <div className="flex flex-col gap-4">
          <fieldset>
            <legend className="text-v2-12 font-medium text-v2-fg-2">
              {isEdit ? 'Dining area' : 'Dining areas'}
            </legend>
            <div
              role={isEdit ? 'radiogroup' : undefined}
              className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-2"
            >
              {areas.map((a) => {
                const label = areaDisplayName(a.area_id, areas);
                const desc = a.environment
                  ? a.environment.charAt(0).toUpperCase() +
                    a.environment.slice(1).toLowerCase()
                  : undefined;
                return isEdit ? (
                  <Radio
                    key={a.area_id}
                    name="blackout-area"
                    label={label}
                    description={desc}
                    checked={draft.dining_area_ids[0] === a.area_id}
                    onChange={() => toggleArea(a.area_id)}
                  />
                ) : (
                  <Checkbox
                    key={a.area_id}
                    label={desc ? `${label} · ${desc}` : label}
                    checked={draft.dining_area_ids.includes(a.area_id)}
                    onChange={() => toggleArea(a.area_id)}
                  />
                );
              })}
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label="Starts">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  className="v2-num"
                  min={today}
                  value={draft.start_date}
                  onChange={(e) =>
                    set({
                      start_date: e.target.value,
                      end_date:
                        draft.end_date < e.target.value
                          ? e.target.value
                          : draft.end_date,
                    })
                  }
                  data-autofocus="true"
                />
              )}
            </Field>
            <Field
              label={draft.all_day ? 'Ends before' : 'Ends'}
              hint={
                draft.all_day
                  ? 'The first day the area is available again.'
                  : undefined
              }
            >
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  className="v2-num"
                  min={draft.start_date}
                  value={draft.end_date}
                  onChange={(e) => set({ end_date: e.target.value })}
                />
              )}
            </Field>
            <div className="col-span-2">
              <Checkbox
                label="All day"
                checked={draft.all_day}
                onChange={(e) =>
                  set(
                    e.target.checked
                      ? {
                          all_day: true,
                          start_time: '00:00',
                          end_time: '00:00',
                          end_date:
                            draft.end_date <= draft.start_date
                              ? addDays(draft.start_date, 1)
                              : draft.end_date,
                        }
                      : {
                          all_day: false,
                          start_time: '18:00',
                          end_time: '21:00',
                          end_date: draft.start_date,
                        },
                  )
                }
              />
            </div>
            {!draft.all_day && (
              <>
                <Field label="From">
                  {(id) => (
                    <Input
                      id={id}
                      type="time"
                      className="v2-num"
                      value={draft.start_time}
                      onChange={(e) => set({ start_time: e.target.value })}
                    />
                  )}
                </Field>
                <Field label="To">
                  {(id) => (
                    <Input
                      id={id}
                      type="time"
                      className="v2-num"
                      value={draft.end_time}
                      onChange={(e) => set({ end_time: e.target.value })}
                    />
                  )}
                </Field>
              </>
            )}
            <Field
              label="Reason"
              hint="Arthur can say this to callers who ask for the area."
              className="col-span-2"
            >
              {(id) => (
                <Textarea
                  id={id}
                  rows={2}
                  placeholder="e.g. Private dinner"
                  value={draft.reason}
                  onChange={(e) => set({ reason: e.target.value })}
                />
              )}
            </Field>
          </div>

          {/* One callout at a time: the error replaces the preview until the draft changes. */}
          {error ? (
            <Callout tone="danger">{error}</Callout>
          ) : (
            <Callout tone="neutral" title="Arthur may say">
              “{blackoutPreview(draft, areas)}”
            </Callout>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default ReservationsSection;
